import {
  ConnectedPosition,
  ConnectionPositionPair,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  Type,
} from '@angular/core';
import { asyncScheduler, fromEvent, Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TipComponent } from './tip.component';
import { fromWindowEvent } from 'core/utils/rxjs/window-event';

type TipPosition = 'before' | 'after' | 'above' | 'below';

const BELOW = {
  originY: 'bottom',
  overlayY: 'top',
  originX: 'center',
  overlayX: 'center',
} as ConnectedPosition;

const ABOVE = {
  originY: 'top',
  overlayY: 'bottom',
  originX: 'center',
  overlayX: 'center',
} as ConnectedPosition;

const AFTER = {
  originY: 'center',
  overlayY: 'center',
  originX: 'end',
  overlayX: 'start',
} as ConnectedPosition;

const BEFORE = {
  originY: 'center',
  overlayY: 'center',
  originX: 'start',
  overlayX: 'end',
} as ConnectedPosition;

@Directive({ selector: '[appTip]' })
export class TipDirective implements OnInit, OnDestroy, OnChanges {
  private static openedTips = new Set<string>();

  @Input('appTip') text = '';
  @Input() tipId?: string;
  @Input() tipTitle?: string;
  @Input() tipPosition: 'above' | 'below' | 'after' | 'before' = 'above';
  @Input() customComponent: Type<TipComponent<unknown>> = TipComponent;
  @Input() tipClass?: string | false;
  @Input() tipData?: unknown;
  @Input() tipTimeout = 350;
  @Input() tipHideTimeout = 200;
  @Input() tipAutoShow = 0;
  @Input() tipAutoHide = 0;
  @Input() tipUnusable = true;
  @Input() tipObsessive: boolean | null = false;
  @Input() tipHandleClick?: boolean;
  @Input() tipDisabled?: boolean;
  @Input() viewportMargin = 10;
  @Input() touchEnabled = true;
  @Input() showOverlay = false;
  @Input() withPush = true;
  @Input() stopTouchPropagation = false;
  @Input() connectedPosition?: ConnectedPosition;

  @Output() tipClosed = new EventEmitter<void>();
  @Output() tipOpened = new EventEmitter<void>();

  get active(): boolean | undefined {
    return this._active;
  }

  private isTouch?: boolean;
  private showTimeout = new Subscription();
  protected hideTimeout$: Subject<void> = new Subject<void>();
  protected windowClickSub$: Subject<void> = new Subject<void>();
  protected autoShowTimeout$: Subject<void> = new Subject<void>();
  protected autoHideTimeout$: Subject<void> = new Subject<void>();
  protected instanceLeaveSub$: Subject<void> = new Subject<void>();
  protected instanceEnterSub$: Subject<void> = new Subject<void>();
  private destroy$ = new Subject<void>();
  private currentPosition?: TipPosition;
  private overlayRef?: OverlayRef;
  private instance?: TipComponent<unknown>;
  private _active?: boolean;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private chRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnChanges(changes: SimpleChanges<TipDirective>): void {
    if (changes.tipDisabled) {
      if (!this.tipDisabled) {
        this.scheduleTipShowIfNeeded();
      }
    }
    if (changes.tipHandleClick) {
      if (this.tipHandleClick) {
        const tipElement = this.instance?.elementRef.nativeElement;
        if (tipElement) {
          this.closeOnClickOutside(tipElement);
        }
      } else {
        this.windowClickSub$.next();
      }
    }
  }

  ngOnDestroy(): void {
    this.forceHide();
    this.destroy$.next();
    this.destroy$.complete();
    this.instanceLeaveSub$.next();
    this.instanceLeaveSub$.complete();
    this.instanceEnterSub$.next();
    this.instanceEnterSub$.complete();
    this.showTimeout.unsubscribe();
    this.hideTimeout$.next();
    this.hideTimeout$.complete();
    this.autoShowTimeout$.next();
    this.autoShowTimeout$.complete();
    this.autoHideTimeout$.next();
    this.autoHideTimeout$.complete();
    this.windowClickSub$.next();
    this.windowClickSub$.complete();
  }

  ngOnInit(): void {
    this.renderer.setStyle(this.elementRef.nativeElement, 'cursor', 'pointer');
    let position = [BELOW, ABOVE];
    if (this.connectedPosition) {
      position = [this.connectedPosition, AFTER, ABOVE, BELOW];
    } else {
      if (this.tipPosition === 'above') {
        position = [ABOVE, BELOW];
      } else if (this.tipPosition === 'after') {
        position = [AFTER, BEFORE, ABOVE, BELOW];
      } else if (this.tipPosition == 'before') {
        position = [BEFORE, AFTER, ABOVE, BELOW];
      }
    }
    this.scheduleTipShowIfNeeded();
    // fromWindowEvent('scroll', { passive: true })
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(() => {
    //     if (this._active) {
    //       this.hide();
    //       this.chRef.detectChanges();
    //     }
    //   });
    fromEvent(this.elementRef.nativeElement, 'touchstart', { passive: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isTouch = true;
      });
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withViewportMargin(this.viewportMargin)
      .withPositions(position)
      .withPush(this.withPush); 
    positionStrategy.positionChanges.pipe(takeUntil(this.destroy$)).subscribe(change => {
      this.updateCurrentPositionClass(change.connectionPair);
    });
    this.overlayRef = this.overlay.create({ positionStrategy });
  }

  scheduleTipShowIfNeeded(): void {
    if (!this.tipAutoShow) {
      return;
    }
    this.autoShowTimeout$.next();
    this.autoHideTimeout$.next();
    timer(this.tipAutoShow)
      .pipe(takeUntil(this.autoShowTimeout$))
      .subscribe(() => {
        if (!this.tipDisabled) {
          this.forceShow();
        }
        if (this.tipAutoHide) {
          timer(this.tipAutoHide)
            .pipe(takeUntil(this.autoHideTimeout$))
            .subscribe(() => {
              this.forceHide();
            });
        }
      });
  }

  @HostListener('mousedown')
  onMousedown(): void {
    if (!this.isTouch) {
      this.showTimeout.unsubscribe();
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if ((this.isTouch && this.touchEnabled) || this.tipHandleClick) {
      if (!this._active) {
        this.show();
      } else {
        this.hide();
      }
    }
    if (this.stopTouchPropagation && this.isTouch) {
      event.stopImmediatePropagation();
    }
  }

  @HostListener('mouseenter')
  onMouseenter(): void {
    if (this.isTouch || this.tipHandleClick) {
      return;
    }
    this.hideTimeout$.next();
    this.showTimeout.unsubscribe();
    this.showTimeout = asyncScheduler.schedule(() => {
      this.show();
    }, this.tipTimeout);
  }

  @HostListener('mouseleave')
  onMouseleave(): void {
    if (this.tipHandleClick) {
      return;
    }
    timer(this.tipHideTimeout)
      .pipe(takeUntil(this.hideTimeout$))
      .subscribe(() => {
        this.showTimeout.unsubscribe();
        this.hide();
      });
  }

  show(): void {
    if (this._active || this.tipDisabled || this.tipObsessive) {
      return;
    }
    this.forceShow();
  }

  forceShow(): void {
    if (this.tipId && TipDirective.openedTips.has(this.tipId)) {
      return;
    }
    const tooltipRef = this.overlayRef?.attach(new ComponentPortal(this.customComponent));
    if (tooltipRef) {
      this.instance = tooltipRef.instance;
      this.instance.title = this.tipTitle;
      this.instance.text = this.text;
      this.instance.data = this.tipData;
      this.instance.class = this.tipClass || 'default';
      this.instance.close = () => {
        this.forceHide();
      };
      this._active = true;
      if (this.tipId) {
        TipDirective.openedTips.add(this.tipId);
      }
      const tipElement = this.instance.elementRef.nativeElement;
      if (!this.tipUnusable) {
        fromEvent(tipElement, 'mouseenter')
          .pipe(takeUntil(this.instanceEnterSub$))
          .subscribe(() => {
            this.hideTimeout$.next();
          });
        fromEvent(tipElement, 'mouseleave')
          .pipe(takeUntil(this.instanceLeaveSub$))
          .subscribe(() => {
            this.onMouseleave();
          });
      }
      if (this.tipHandleClick) {
        this.closeOnClickOutside(tipElement);
      }
      if (this.showOverlay) {
        this.renderer.addClass(this.document.body, 'cdk-overlay');
      }
      this.renderer.addClass(this.elementRef.nativeElement, 'anchor-active');
    }
    this.tipOpened.next();
  }

  private closeOnClickOutside(tipElement: HTMLElement): void {
    const anchorElement = this.elementRef.nativeElement as HTMLElement;
    fromWindowEvent('mousedown')
      .pipe(takeUntil(this.windowClickSub$))
      .subscribe(e => {
        const clickedInside = tipElement.contains(e.target as Node) || anchorElement.contains(e.target as Node);
        if (!clickedInside) {
          this.hide();
        }
      });
  }

  hide(): void {
    if (!this._active || this.tipObsessive) {
      return;
    }
    this.forceHide();
  }

  forceHide(): void {
    if (this.instance) {
      this.tipClosed.next();
    }
    this.overlayRef?.detach();
    this._active = false;
    this.currentPosition = undefined;
    this.instance = undefined;
    this.hideTimeout$.next();
    this.windowClickSub$.next();
    this.instanceLeaveSub$.next();
    this.instanceEnterSub$.next();
    if (this.tipId) {
      TipDirective.openedTips.delete(this.tipId);
    }
    if (this.showOverlay) {
      this.renderer.removeClass(this.document.body, 'cdk-overlay');
    }
    this.renderer.removeClass(this.elementRef.nativeElement, 'anchor-active');
  }

  private updateCurrentPositionClass(connectionPair: ConnectionPositionPair): void {
    const { overlayY, originX, originY } = connectionPair;
    let newPosition: TipPosition;
    if (overlayY === 'center') {
      newPosition = originX === 'start' ? 'before' : 'after';
    } else {
      newPosition = overlayY === 'bottom' && originY === 'top' ? 'above' : 'below';
    }

    if (newPosition !== this.currentPosition) {
      const overlayRef = this.overlayRef;
      if (overlayRef && this.instance) {
        this.renderer.removeClass(this.instance.elementRef.nativeElement, `${this.currentPosition}`);
        this.renderer.addClass(this.instance.elementRef.nativeElement, newPosition);
      }
      this.currentPosition = newPosition;
    }
  }
}
